/**
 * 署名 URL 経由の Range 取得を自前で行う転送層。
 *
 * pdf.js に URL を渡すと取得は pdf.js 側の裁量になり、(1) 署名 URL の失効を吸収できない、
 * (2) Range が使われるかがサーバ／プロキシのヘッダ事情に左右される。ここで Range 要求を
 * 明示的に発行することで、失効時はマニフェスト再取得で URL を貼り替え、取得は常に
 * 単一レンジ（206）に固定する（FR-SRC-02）。
 *
 * 取得そのものと「Range が使えるかの判断」は pdf.js を import しない `range-fetch.ts` にある
 * （この層は `PDFDataRangeTransport` を継承するため jsdom では読み込めない）。
 */
import { PDFDataRangeTransport } from 'pdfjs-dist';
import type { ManifestSession } from '../../core/manifest';
import { DEFAULT_CHUNK, fetchRange, openInput } from './range-fetch';

export { DEFAULT_CHUNK } from './range-fetch';

/**
 * 転送の開き方（FR-SRC-02）。Range が使えない経路では全体取得へ落ちる。
 */
export type OpenedTransport =
  | { kind: 'range'; transport: SignedRangeTransport }
  | { kind: 'full'; data: Uint8Array };

/** pdf.js へ渡す転送オブジェクト。 */
export class SignedRangeTransport extends PDFDataRangeTransport {
  /** 取得済みバイト数（起動表示の進捗の分子）。 */
  private loaded: number;

  private constructor(
    private readonly session: ManifestSession,
    private readonly controller: AbortController,
    /** 取得失敗の通知先（pdf.js 側に失敗を伝える経路が無いため、呼び出し元へ回す）。 */
    private readonly onError: (error: unknown) => void,
    length: number,
    initialData: Uint8Array,
    /**
     * 取得の進み具合の通知先。pdf.js 6 の PDFDataRangeTransport は進捗を報告する口を
     * 持たない（loadingTask.onProgress は Range 転送では発火しない）ため、実際に
     * Range を発行しているこの層から直接伝える。
     */
    private readonly onProgress?: (loaded: number, total: number) => void,
  ) {
    super(length, initialData);
    this.loaded = initialData.length;
  }

  /**
   * 先頭チャンクを取得して全体長を確定し、転送を用意する。
   * サーバが Range を返さなければ、その応答（＝全体）を持って全体取得へ落とす。
   */
  static async open(
    session: ManifestSession,
    onError: (error: unknown) => void,
    onProgress?: (loaded: number, total: number) => void,
    chunkSize: number = DEFAULT_CHUNK,
  ): Promise<OpenedTransport> {
    const controller = new AbortController();
    const input = await openInput(session, controller.signal, chunkSize);
    if (input.kind === 'full') {
      onProgress?.(input.data.length, input.data.length);
      return { kind: 'full', data: input.data };
    }
    const transport = new SignedRangeTransport(
      session,
      controller,
      onError,
      input.totalLength,
      input.initial,
      onProgress,
    );
    transport.progressed(0); // 先頭チャンクぶんを最初の 1 回として伝える
    return { kind: 'range', transport };
  }

  override requestDataRange(begin: number, end: number): void {
    void fetchRange(this.session, begin, end, this.controller.signal)
      .then((range) => {
        this.progressed(range.body.length);
        this.onDataRange(begin, range.body);
      })
      .catch((error: unknown) => {
        if (this.controller.signal.aborted) return;
        this.onError(error);
      });
  }

  private progressed(bytes: number): void {
    this.loaded = Math.min(this.loaded + bytes, this.length);
    this.onProgress?.(this.loaded, this.length);
  }

  override abort(): void {
    this.controller.abort();
  }
}
